import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalCombobox } from '../../components/FormField/PortalCombobox';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { useToast } from '../../components/Toast/ToastContext';
import { lbsToKg, kgToLbs, speciesEmoji } from '../../lib/utils';
import { createPetMutation, updatePetMutation, rescuePetsQuery, allBreedsQuery } from '../../lib/rescueQueries';
import type { RescuePetsData, AllBreedsData, RescuePetPhotos } from '../../lib/rescueQueries';
import { trackPetCreated, trackPetEdited } from '../../analytics';
// Reuse the nano pet-photo primitives: a headless file-picker/preview and the
// upload→setPetPrimaryPhoto/removePhoto orchestration. Both are pet-CRUD-agnostic
// (they don't touch nano's create/update mutations), so the rescue portal keeps
// its own createPet/updatePet and only borrows the photo mechanism.
import ImageUpload, { type ImageAction } from '../../../appview/components/ImageUpload/ImageUpload';
import { useUpdatePetPhoto } from '../../../appview/lib/petMutations';
import styles from './PetFormModal.module.scss';

export interface PetFormPet {
  id: string;
  name: string;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  additionalInfo?: string | null;
  breed?: { name: string } | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  photos?: RescuePetPhotos | null;
}

type PetFormModalProps =
  | {
      open: boolean;
      onClose: () => void;
      mode: 'create';
      /**
       * Called after the pet is successfully created. When provided, the parent
       * owns post-creation messaging and modal close — PetFormModal will skip
       * its default success toast and `onClose()` so the parent can chain
       * follow-up work (e.g. assigning a device to the new pet).
       */
      onCreated?: (pet: { id: string; name: string }) => void | Promise<void>;
    }
  | { open: boolean; onClose: () => void; mode: 'edit'; pet: PetFormPet | null };

function parseSpayNeuter(additionalInfo?: string | null): string {
  if (!additionalInfo) {
    return 'Unknown';
  }
  const lower = additionalInfo.toLowerCase();
  if (lower.includes('spayed')) {
    return 'Spayed';
  }
  if (lower.includes('neutered')) {
    return 'Neutered';
  }
  if (lower.includes('intact')) {
    return 'Intact';
  }
  return 'Unknown';
}

export default function PetFormModal(props: PetFormModalProps) {
  const { open, onClose, mode } = props;
  const pet = mode === 'edit' ? props.pet : null;
  const { showToast } = useToast();

  const [name, setName] = useState('');
  const [species, setSpecies] = useState('DOG');
  const [gender, setGender] = useState('FEMALE');
  const [weight, setWeight] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [breedId, setBreedId] = useState('');
  const [color, setColor] = useState('');
  const [spayNeuter, setSpayNeuter] = useState('Unknown');
  // Pending photo change driven by the picker's onChange: undefined = untouched,
  // 'replace' = new file to upload, 'remove' = clear the existing photo. Reset on
  // open; only replace/remove are applied on save (see applyPhoto).
  const [photoAction, setPhotoAction] = useState<ImageAction | undefined>(undefined);
  const client = useApolloClient();
  const [updatePetPhoto, { loading: photoSaving }] = useUpdatePetPhoto();

  // The pet's current photo, as a "preserve" action. Computed synchronously from
  // props (not in an effect) so it's available at mount — ImageUpload seeds its
  // preview from initialValue once, so an effect would run too late and the
  // existing photo wouldn't show. Undefined in create mode / when there's none.
  const initialPhotoAction = useMemo<ImageAction | undefined>(() => {
    const existing = mode === 'edit' ? pet?.photos?.first : null;
    return existing
      ? { kind: 'preserve', photo: { id: existing.id, image: { fullSize: existing.image?.fullSize ?? null } } }
      : undefined;
  }, [mode, pet]);

  // Only needed in create mode to derive the rescue household id.
  //
  // errorPolicy 'all' keeps partial data when the response carries unrelated
  // GraphQL errors — e.g. a pet's chip contact-history entry with a null value
  // in a non-nullable field. With the default policy Apollo discards the entire
  // result on any error, leaving `householdId` null and permanently disabling
  // the Create Pet button even though the rescue household resolved fine.
  const { data: petsData } = useQuery<RescuePetsData>(rescuePetsQuery, {
    skip: mode !== 'create',
    errorPolicy: 'all',
  });
  const { data: breedsData } = useQuery<AllBreedsData>(allBreedsQuery, {
    variables: { species },
    skip: !open || (mode === 'edit' && !pet),
  });

  const householdId = useMemo(() => {
    // Optional-chained: a partial (errored) response may null out branches.
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    return rescueHousehold?.household.id ?? null;
  }, [petsData]);

  const breedOptions = useMemo(() => {
    const list = breedsData?.allBreeds ?? [];
    const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.map((b) => ({ value: b.id, label: b.name }));
  }, [breedsData]);

  const otherBreedId = useMemo(() => {
    return breedOptions.find((b) => b.label === 'Other')?.value ?? '';
  }, [breedOptions]);

  // Reset (create) or pre-fill (edit) the form whenever the modal opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'create') {
      setName('');
      setSpecies('DOG');
      setGender('FEMALE');
      setWeight('');
      setBirthYear('');
      setBirthMonth('');
      setBirthDay('');
      setBreedId('');
      setColor('');
      setSpayNeuter('Unknown');
      setPhotoAction(undefined);
      return;
    }
    if (!pet) {
      return;
    }
    setName(pet.name ?? '');
    setSpecies(pet.species ?? 'DOG');
    setGender(pet.gender ?? 'FEMALE');
    const lbs = kgToLbs(pet.weight);
    setWeight(lbs ? String(lbs) : '');
    setBirthYear(pet.yearOfBirth ? String(pet.yearOfBirth) : '');
    setBirthMonth(pet.monthOfBirth ? String(pet.monthOfBirth) : '');
    setBirthDay(pet.dayOfBirth ? String(pet.dayOfBirth) : '');
    setColor(pet.color ?? '');
    setSpayNeuter(parseSpayNeuter(pet.additionalInfo));
    setBreedId('');
    // Clear any pending photo change from a previous edit; the existing photo is
    // shown via the picker's initialValue (initialPhotoAction), not this state.
    setPhotoAction(undefined);
  }, [open, mode, pet]);

  // In edit mode, resolve the pet's breed name → breed id once breeds load.
  useEffect(() => {
    if (mode !== 'edit' || !pet?.breed?.name || breedOptions.length === 0) {
      return;
    }
    const match = breedOptions.find((b) => b.label === pet.breed?.name);
    if (match) {
      setBreedId(match.value);
    }
  }, [mode, pet, breedOptions]);

  const [createPet, { loading: creating }] = useMutation<{ createPet: { id: string; name: string } }>(
    createPetMutation,
  );
  const [updatePet, { loading: saving }] = useMutation(updatePetMutation);
  const submitting = creating || saving || photoSaving;

  // Attach/remove the pet's photo. Only fires for a real change (replace/remove)
  // — a "preserve" action is a no-op. The caller refetches the lists once
  // afterward (so the refresh reflects the new photo), and treats a failure here
  // as non-fatal since the pet itself is already saved.
  async function applyPhoto(petId: string) {
    if (!photoAction || photoAction.kind === 'preserve') {
      return;
    }
    await updatePetPhoto(petId, photoAction);
  }

  async function handleSubmit() {
    if (!name.trim() || !birthYear || !birthMonth) {
      return;
    }
    const additionalParts: string[] = [];
    if (spayNeuter && spayNeuter !== 'Unknown') {
      additionalParts.push(spayNeuter);
    }
    // In edit mode, send null for blank optional fields so the user can
    // clear them. In create mode, send undefined so the field is omitted.
    const blank = mode === 'edit' ? null : undefined;

    // Birth date — schema vs. resolver:
    //   - GraphQL schema (CreatePetInput) declares `yearOfBirth: Int!` and
    //     `monthOfBirth: Int` (year required, month nullable). UpdatePetInput
    //     declares both as nullable.
    //   - The resolver (`validateBirthDateInputs` in
    //     web/src/lib/pet/services/mutators.ts) requires year AND month
    //     together when any birth field is provided. That validator is
    //     gated behind the `pet.enableBirthDateValidation` feature flag,
    //     which is currently ON in production — so the runtime contract is
    //     stricter than the schema suggests.
    // We require year + month in the form to match the active resolver
    // contract; day stays optional.
    const baseInput = {
      name: name.trim(),
      species,
      gender,
      weight: lbsToKg(weight ? parseFloat(weight) : 0),
      yearOfBirth: birthYear ? parseInt(birthYear, 10) : blank,
      monthOfBirth: birthMonth ? parseInt(birthMonth, 10) : blank,
      dayOfBirth: birthDay ? parseInt(birthDay, 10) : blank,
      color: color.trim() || blank,
      additionalInfo: additionalParts.join(', ') || blank,
    };

    try {
      // Step 1: create/update the pet itself. A throw here is a genuine
      // pet-save failure and is reported as such by the outer catch.
      let petId: string;
      let created: { id: string; name: string } | undefined;
      if (mode === 'create') {
        if (!householdId) {
          showToast('Unable to find your rescue household. Please try again later.');
          return;
        }
        const result = await createPet({
          variables: {
            input: {
              ...baseInput,
              breedId: breedId || otherBreedId || undefined,
              isPurebred: false,
              householdId,
            },
          },
        });
        trackPetCreated(species);
        created = result.data?.createPet;
        if (!created) {
          return;
        }
        petId = created.id;
      } else {
        if (!pet) {
          return;
        }
        await updatePet({
          variables: {
            input: {
              ...baseInput,
              id: pet.id,
              breedId: breedId || null,
            },
          },
        });
        trackPetEdited(species);
        petId = pet.id;
      }

      // Step 2: attach the photo. The pet is already saved, so a photo failure
      // must NOT read as a pet-save failure or block close/hand-off — surface a
      // soft warning and carry on.
      const verb = mode === 'create' ? 'created' : 'updated';
      let photoFailed = false;
      try {
        await applyPhoto(petId);
      } catch (photoErr) {
        photoFailed = true;
        showToast(
          `${name.trim()} ${verb}, but the photo couldn’t be saved (${
            photoErr instanceof Error ? photoErr.message : 'upload error'
          }). Try again from Edit.`,
        );
      }

      // Step 3: one refresh, after the photo, so the lists reflect the pet AND
      // its new photo in a single pass (no double rescuePets fetch / flash).
      // Fire-and-forget: the pet is already saved, so a rejected refetch must
      // not read as a save failure or block close / the create hand-off — and
      // awaiting it here also left the submit button re-enabled (submitting
      // tracks only the mutations, which have resolved) while the modal was
      // still open, allowing a duplicate create on a second click. Each active
      // query surfaces its own loading/error state.
      client.refetchQueries({ include: 'active' }).catch(() => {
        // Ignored — see above.
      });

      // Create hand-off: let the parent chain follow-up work (e.g. device
      // assignment). Rejections are swallowed — the parent owns its own errors.
      if (props.mode === 'create' && props.onCreated && created) {
        try {
          await props.onCreated(created);
        } catch {
          // Parent owns error display.
        }
        return;
      }

      if (!photoFailed) {
        showToast(`${name.trim()} ${mode === 'create' ? 'created successfully' : 'updated'}`);
      }
      onClose();
    } catch (err) {
      const verb = mode === 'create' ? 'create' : 'update';
      showToast(`Failed to ${verb} pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  const canSubmit =
    name.trim().length > 0 &&
    birthYear.length > 0 &&
    birthMonth.length > 0 &&
    (mode !== 'create' || !!householdId) &&
    !submitting;

  if (mode === 'edit' && !pet) {
    return null;
  }

  const title = mode === 'create' ? 'Add Pet' : 'Edit Pet Profile';
  const subtitle =
    mode === 'create'
      ? 'Create a pet profile for your rescue. You can add a microchip or device later.'
      : `Update ${pet?.name ?? 'this pet'}’s profile details.`;
  const submitLabel =
    mode === 'create' ? (creating ? 'Creating...' : 'Create Pet') : saving ? 'Saving...' : 'Save Changes';

  return (
    <Modal open={open} onClose={onClose} size="wide">
      <ModalTitle>{title}</ModalTitle>
      <p className={styles.subtitle}>{subtitle}</p>

      {/* Profile photo */}
      <ImageUpload initialValue={initialPhotoAction} onChange={setPhotoAction} onError={(msg) => showToast(msg)}>
        {({ src, openUploadDialog, removePhoto }) => (
          <div className={styles.photoPicker}>
            <button
              type="button"
              className={styles.photoAvatar}
              onClick={openUploadDialog}
              aria-label={src ? 'Update pet photo' : 'Add pet photo'}
            >
              {src ? <img src={src} alt="" className={styles.photoImg} /> : <span>{speciesEmoji(species)}</span>}
            </button>
            <div className={styles.photoActions}>
              <Button variant="ghost" size="sm" onClick={openUploadDialog}>
                {src ? 'Update photo' : 'Add photo'}
              </Button>
              {src && (
                <Button variant="ghost" size="sm" onClick={removePhoto}>
                  Remove
                </Button>
              )}
            </div>
          </div>
        )}
      </ImageUpload>

      {/* Name + Species */}
      <div className={styles.row2}>
        <PortalField label="Pet Name *">
          <PortalInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Biscuit" autoFocus />
        </PortalField>
        <PortalField label="Species *">
          <ToggleGroup
            options={[
              { value: 'DOG', label: 'Dog' },
              { value: 'CAT', label: 'Cat' },
            ]}
            value={species}
            onChange={(val) => {
              setSpecies(val);
              setBreedId('');
            }}
            style={{ marginTop: 2 }}
          />
        </PortalField>
      </div>

      {/* Breed + Gender */}
      <div className={styles.row2}>
        <PortalField label="Breed">
          <PortalCombobox
            options={breedOptions}
            value={mode === 'create' ? breedId || otherBreedId : breedId}
            onChange={setBreedId}
            placeholder="e.g. Lab Mix"
          />
        </PortalField>
        <PortalField label="Gender">
          <PortalSelect
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              if (e.target.value === 'MALE' && spayNeuter === 'Spayed') {
                setSpayNeuter('Unknown');
              }
              if (e.target.value === 'FEMALE' && spayNeuter === 'Neutered') {
                setSpayNeuter('Unknown');
              }
            }}
          >
            <option value="FEMALE">Female</option>
            <option value="MALE">Male</option>
          </PortalSelect>
        </PortalField>
      </div>

      {/* Birth Year / Month / Day */}
      <div className={styles.row3}>
        <PortalField label="Birth Year *">
          <PortalInput
            type="number"
            min="2000"
            max={new Date().getFullYear()}
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            placeholder="e.g. 2023"
          />
        </PortalField>
        <PortalField label="Month *">
          <PortalInput
            type="number"
            min="1"
            max="12"
            value={birthMonth}
            onChange={(e) => setBirthMonth(e.target.value)}
            placeholder="e.g. 3"
          />
        </PortalField>
        <PortalField label="Day" className={styles.optionalField}>
          <PortalInput
            type="number"
            min="1"
            max="31"
            value={birthDay}
            onChange={(e) => setBirthDay(e.target.value)}
            placeholder="Optional"
          />
        </PortalField>
      </div>

      {mode === 'edit' && (
        <PortalField label="Spay/Neuter Status *">
          <PortalSelect value={spayNeuter} onChange={(e) => setSpayNeuter(e.target.value)}>
            {gender === 'FEMALE' && <option value="Spayed">Spayed</option>}
            {gender === 'MALE' && <option value="Neutered">Neutered</option>}
            <option value="Intact">Intact</option>
            <option value="Unknown">Unknown</option>
          </PortalSelect>
        </PortalField>
      )}
      <PortalField label="Color">
        <PortalInput value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Black & White" />
      </PortalField>

      {/* Weight */}
      <PortalField label="Weight (lbs)">
        <PortalInput
          type="number"
          min="0"
          step="0.1"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          placeholder="Optional"
        />
      </PortalField>

      {mode === 'create' && (
        <div className={styles.infoBanner}>
          Creating a pet profile first means you won&rsquo;t need to re-enter this data later.
        </div>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </ModalActions>
    </Modal>
  );
}
